import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signIn,
  submitMfaCode,
  completeNewPassword,
  forgotPassword,
  confirmForgotPassword,
  type MfaChallenge,
} from './cognito';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import { useAuth } from './AuthContext';

type Step = 'credentials' | 'mfa' | 'new_password' | 'forgot_request' | 'forgot_confirm';

export default function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [info, setInfo] = useState<string | null>(null);

  async function handleCredentialsSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(email, password);
      if (result.status === 'mfa_required') {
        setChallenge(result.challenge);
        setStep('mfa');
      } else if (result.status === 'new_password_required') {
        setPendingUser(result.cognitoUser);
        setStep('new_password');
      } else {
        await refresh();
        navigate('/', { replace: true });
      }
    } catch {
      // Generic message: never reveal whether the email exists or which factor failed.
      setError('Sign-in failed. Check your email and password and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      await submitMfaCode(challenge, mfaCode);
      await refresh();
      navigate('/', { replace: true });
    } catch {
      setError('Invalid authentication code. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleNewPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pendingUser) return;
    setError(null);
    setBusy(true);
    try {
      await completeNewPassword(pendingUser, newPassword);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set a new password.');
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotRequestSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(forgotEmail);
      setInfo(`If an account exists for ${forgotEmail}, a verification code has been sent to it.`);
      setStep('forgot_confirm');
    } catch {
      // Same generic message on failure as on success: never reveal whether the email exists.
      setInfo(`If an account exists for ${forgotEmail}, a verification code has been sent to it.`);
      setStep('forgot_confirm');
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotConfirmSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmForgotPassword(forgotEmail, resetCode, resetPassword);
      setInfo('Your password has been reset. Sign in with your new password.');
      setStep('credentials');
      setPassword('');
      setResetCode('');
      setResetPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset the password. Check the code and try again.');
    } finally {
      setBusy(false);
    }
  }

  function startForgotPassword() {
    setError(null);
    setInfo(null);
    setForgotEmail(email);
    setStep('forgot_request');
  }

  function backToSignIn() {
    setError(null);
    setInfo(null);
    setStep('credentials');
  }

  return (
    <div style={{ maxWidth: 420, margin: '80px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <img src="/phoenix-logo.png" alt="Phoenix Consulting" style={{ height: 40 }} />
      </div>
      <div className="card">
        <h1 className="login-title">Phoenix Questionnaire</h1>
        {error && <div className="error-banner">{error}</div>}
        {info && <div className="success-banner">{info}</div>}

        {step === 'credentials' && (
          <form onSubmit={handleCredentialsSubmit}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
              <button type="submit" className="primary" disabled={busy} style={{ flex: 'none', minWidth: 0 }}>
                {busy ? 'Signing in...' : 'Sign in'}
              </button>
              <button
                type="button"
                onClick={startForgotPassword}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textDecoration: 'underline',
                  minHeight: 'auto',
                  flex: 'none',
                  minWidth: 0,
                  opacity: 0.6,
                }}
              >
                Forgot password?
              </button>
            </div>
          </form>
        )}

        {step === 'mfa' && (
          <form onSubmit={handleMfaSubmit}>
            <p className="muted">Enter the 6-digit code from your authenticator app.</p>
            <div className="field">
              <label htmlFor="mfa">Authentication code</label>
              <input
                id="mfa"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
              />
            </div>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        )}

        {step === 'new_password' && (
          <form onSubmit={handleNewPasswordSubmit}>
            <p className="muted">Your account requires a new password before you can sign in.</p>
            <div className="field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                required
                minLength={14}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Saving...' : 'Set password'}
            </button>
          </form>
        )}

        {step === 'forgot_request' && (
          <form onSubmit={handleForgotRequestSubmit}>
            <p className="muted">Enter your admin email and we'll send a verification code to reset your password.</p>
            <div className="field">
              <label htmlFor="forgotEmail">Email</label>
              <input
                id="forgotEmail"
                type="email"
                autoComplete="username"
                required
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
            </div>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Sending...' : 'Send code'}
            </button>
            <button
              type="button"
              onClick={backToSignIn}
              style={{ background: 'none', border: 'none', marginTop: 12, padding: 0, textDecoration: 'underline', minHeight: 'auto' }}
            >
              Back to sign in
            </button>
          </form>
        )}

        {step === 'forgot_confirm' && (
          <form onSubmit={handleForgotConfirmSubmit}>
            <p className="muted">Enter the verification code sent to {forgotEmail} and choose a new password.</p>
            <div className="field">
              <label htmlFor="resetCode">Verification code</label>
              <input
                id="resetCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="resetPassword">New password</label>
              <input
                id="resetPassword"
                type="password"
                autoComplete="new-password"
                required
                minLength={14}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Resetting...' : 'Reset password'}
            </button>
            <button
              type="button"
              onClick={backToSignIn}
              style={{ background: 'none', border: 'none', marginTop: 12, padding: 0, textDecoration: 'underline', minHeight: 'auto' }}
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
